import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { ChevronRight, Loader2 } from 'lucide-react';
import Logo from '@/components/Logo';
import { toast } from 'sonner';

const Orb = ({ x, y, size, color, delay }: {
    x: string; y: string; size: number; color: string; delay: number;
}) => (
    <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{ left: x, top: y, width: size, height: size, background: color, filter: 'blur(90px)' }}
        animate={{ scale: [1, 1.2, 1], opacity: [0.15, 0.3, 0.15] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay }}
    />
);

const GlassCard = ({
    children, onClick, className = '', type = 'button',
}: { children: React.ReactNode; onClick?: () => void; className?: string; type?: 'button' | 'submit' }) => (
    <motion.button
        type={type}
        onClick={onClick}
        whileTap={{ scale: 0.98, y: 1 }}
        whileHover={{ scale: 1.02, backgroundColor: 'rgba(255,255,255,0.08)' }}
        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        className={`w-full text-left rounded-[24px] p-5 select-none cursor-pointer transition-all duration-300 ${className}`}
        style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.1), 0 10px 40px rgba(0,0,0,0.4)',
        }}
    >
        {children}
    </motion.button>
);

const AuthPage = () => {
    const {
        isAuthenticated, isLoading,
        loginWithGoogle, loginWithEmail, signUpWithEmail, loginAsGuest,
    } = useAuth();
    const navigate = useNavigate();

    const [mode, setMode] = useState<'signin' | 'signup'>('signin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!isLoading && isAuthenticated) navigate('/app', { replace: true });
    }, [isAuthenticated, isLoading, navigate]);

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        const res = mode === 'signin'
            ? await loginWithEmail(email, password)
            : await signUpWithEmail(email, password, name);
        setBusy(false);
        if (res.error) {
            toast.error(res.error);
        } else if (mode === 'signup') {
            toast.success('Check your email to confirm your account.');
        }
    };

    return (
        <div className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden bg-[#1A1310] overflow-y-auto">
            <div className="absolute inset-0 opacity-40 mix-blend-overlay pointer-events-none"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}
            />
            <Orb x="-10%" y="-10%" size={600} color="radial-gradient(circle, rgba(194,149,107,0.3) 0%, transparent 70%)" delay={0} />
            <Orb x="60%" y="40%" size={500} color="radial-gradient(circle, rgba(191,90,242,0.2) 0%, transparent 70%)" delay={2} />
            <Orb x="10%" y="60%" size={450} color="radial-gradient(circle, rgba(48,209,88,0.15) 0%, transparent 70%)" delay={4} />
            <div className="absolute inset-0 pointer-events-none z-0"
                style={{ background: 'radial-gradient(circle at center, transparent 0%, rgba(10,10,15,0.4) 40%, #1A1310 100%)' }}
            />

            <div className="relative z-10 w-full max-w-sm px-6 py-10 flex flex-col gap-8">
                <motion.div
                    initial={{ opacity: 0, y: -20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="text-center space-y-3"
                >
                    <div className="relative mx-auto w-20 h-20">
                        <div className="absolute -inset-4 bg-[#C2956B]/20 blur-[32px] rounded-full animate-pulse" />
                        <div
                            className="relative w-full h-full rounded-[28px] flex items-center justify-center overflow-hidden backdrop-blur-3xl shadow-2xl"
                            style={{
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.02) 100%)',
                                border: '1px solid rgba(255,255,255,0.15)',
                            }}
                        >
                            <Logo className="h-10 w-10" animate />
                        </div>
                    </div>
                    <h1
                        className="text-[36px] font-black tracking-[-0.04em] leading-tight"
                        style={{
                            fontFamily: "'Outfit', sans-serif",
                            background: 'linear-gradient(to bottom, #FFFFFF 0%, rgba(255,255,255,0.6) 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Togetherly
                    </h1>
                    <p className="text-[12px] font-black text-white/40 uppercase tracking-[0.2em]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        Watch the world together
                    </p>
                </motion.div>

                {/* Google */}
                <GlassCard onClick={() => loginWithGoogle()} className="group">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-white/5 border border-white/10">
                            <svg viewBox="0 0 24 24" className="w-6 h-6">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <p className="text-[16px] font-black text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                Continue with Google
                            </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-white/30" />
                    </div>
                </GlassCard>

                {/* Divider */}
                <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">or</span>
                    <div className="flex-1 h-px bg-white/10" />
                </div>

                {/* Email form */}
                <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3">
                    {mode === 'signup' && (
                        <input
                            type="text"
                            placeholder="Display name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-5 py-4 rounded-2xl bg-white/[0.04] border border-white/[0.08] text-white placeholder-white/30 text-[15px] focus:outline-none focus:border-[#C2956B]/50 transition-colors backdrop-blur-2xl"
                            style={{ fontFamily: "'Outfit', sans-serif" }}
                        />
                    )}
                    <input
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-5 py-4 rounded-2xl bg-white/[0.04] border border-white/[0.08] text-white placeholder-white/30 text-[15px] focus:outline-none focus:border-[#C2956B]/50 transition-colors backdrop-blur-2xl"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                    />
                    <input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        className="w-full px-5 py-4 rounded-2xl bg-white/[0.04] border border-white/[0.08] text-white placeholder-white/30 text-[15px] focus:outline-none focus:border-[#C2956B]/50 transition-colors backdrop-blur-2xl"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                    />

                    <button
                        type="submit"
                        disabled={busy}
                        className="w-full py-4 rounded-2xl bg-[#C2956B] hover:bg-[#C2956B]/90 text-white text-[15px] font-black tracking-tight transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-[0_8px_30px_rgba(194,149,107,0.4)]"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                        {mode === 'signin' ? 'Sign In' : 'Create Account'}
                    </button>

                    <button
                        type="button"
                        onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
                        className="text-[12px] font-bold text-white/40 hover:text-white/70 transition-colors py-2"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                        {mode === 'signin' ? "New here? Create an account" : 'Already have an account? Sign in'}
                    </button>
                </form>

                {/* Guest */}
                <button
                    onClick={loginAsGuest}
                    className="text-center text-[12px] font-black text-white/30 hover:text-white/60 uppercase tracking-[0.2em] transition-colors py-2"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                    Continue as guest →
                </button>

                <p className="text-center text-[10px] text-white/30 font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    By engaging, you accept our <Link to="/terms" className="text-white/50 underline">Terms</Link> & <Link to="/privacy" className="text-white/50 underline">Privacy</Link>
                </p>
            </div>
        </div>
    );
};

export default AuthPage;
